import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import { AvatarDropdown, AvatarName, Footer, SelectLang } from '@/components';
import AuthGuard from '@/components/AuthGuard';
import {
  getAdminRoutes,
  getCurrentUser,
} from '@/services/ant-design-pro/login';
import '@ant-design/v5-patch-for-react-19';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { API_SERVERS, DEVELOPMENT_API_ORIGIN } from '../config/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  adminInfo?: API.AdminInfo;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  menus?: API.MenuDTO[];
}> {
  let currentUserResponse: API.CurrentUserResponse | undefined;

  const fetchUserInfo = async () => {
    try {
      // 检查是否有 token
      const token = localStorage.getItem('auth_token');
      if (!token) {
        history.push(loginPath);
        return undefined;
      }

      // 从后端 API 获取用户信息
      const result = await getCurrentUser({
        skipErrorHandler: true,
      });

      if (result.code !== 200 || !result.data) {
        // 如果获取失败，清除 token 并跳转到登录页
        localStorage.clear();
        history.push(loginPath);
        return undefined;
      }

      currentUserResponse = result.data;
      const { userInfo } = result.data;

      // 转换为 CurrentUser 格式
      return {
        name: userInfo.nickname,
        avatar: userInfo.avatar || undefined,
        userid: String(userInfo.userId),
      };
    } catch (_error) {
      // 如果出错，清除本地数据并跳转到登录页
      localStorage.clear();
      history.push(loginPath);
    }
    return undefined;
  };

  const fetchMenus = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        return [];
      }

      const result = await getAdminRoutes({
        skipErrorHandler: true,
      });

      if (result.code === 200) {
        return result.data || [];
      }
      return [];
    } catch (error) {
      console.error('获取菜单失败:', error);
      return [];
    }
  };

  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    const menus = await fetchMenus();

    // 获取管理员信息
    const adminInfo = currentUserResponse?.adminInfo;

    return {
      fetchUserInfo,
      currentUser,
      adminInfo,
      menus,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 根据管理员类型生成标题
  const getTitle = () => {
    const adminInfo = initialState?.adminInfo;
    if (!adminInfo) return '台球平台';

    switch (adminInfo.adminType) {
      case 'PLATFORM':
        return '台球平台';
      case 'BRAND':
        return adminInfo.brandName || '台球平台';
      case 'STORE':
        return adminInfo.storeName || '台球平台';
      default:
        return '台球平台';
    }
  };

  return {
    actionsRender: () => [<SelectLang key="SelectLang" />],
    title: getTitle(),
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    // 自定义菜单数据，使用从服务器获取的菜单
    menu: {
      locale: false,
      params: initialState,
      request: async () => {
        const menus = initialState?.menus || [];
        // 将菜单数据转换为 ProLayout 需要的格式
        return menus.map((menu: any) => transformMenuToLayoutMenu(menu));
      },
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;

      const menus = initialState?.menus || [];
      const noAuthPaths = [
        loginPath,
        '/user/register',
        '/user/register-result',
        '/',
        '/404',
      ];

      return (
        <>
          <AuthGuard menus={menus} noAuthPaths={noAuthPaths}>
            {children}
          </AuthGuard>
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * 将菜单数据转换为 ProLayout 需要的格式
 */
function transformMenuToLayoutMenu(menu: any): any {
  const layoutMenu: any = {
    path: menu.path,
    name: menu.menuName,
    icon: menu.icon,
  };

  // 递归处理子菜单
  if (menu.children && menu.children.length > 0) {
    layoutMenu.children = menu.children.map(transformMenuToLayoutMenu);
  }

  return layoutMenu;
}

/**
 * 将菜单数据转换为路由配置
 */
function transformMenuToRoute(menu: any): any {
  const route: any = {
    path: menu.path,
    name: menu.menuName,
    icon: menu.icon,
  };

  // 如果有组件路径，添加组件配置
  if (menu.component) {
    route.component = `./pages/${menu.component}`;
  }

  // 递归处理子菜单
  if (menu.children && menu.children.length > 0) {
    route.routes = menu.children.map(transformMenuToRoute);
  }

  return route;
}

/**
 * 修改客户端路由
 * @see https://umijs.org/docs/api/runtime-config#patchclientroutes-routes-
 */
export function patchClientRoutes({ routes }: any) {
  // 从 window 获取初始状态中的菜单数据
  const initialState = (window as any).g_initialState;
  const menus = initialState?.menus || [];

  if (menus.length === 0) {
    return;
  }

  // 将菜单转换为路由配置
  const menuRoutes = menus.map(transformMenuToRoute);

  // 找到根路由
  const rootRoute = routes.find((route: any) => route.path === '/');

  if (rootRoute) {
    // 移除原有的静态路由（保留登录页和404页）
    const staticRoutes =
      rootRoute.routes?.filter(
        (route: any) => route.path === '/user' || route.path === '*',
      ) || [];

    // 添加动态菜单路由
    rootRoute.routes = [...menuRoutes, ...staticRoutes];
  }
}

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 开发后端由 config/api.ts 控制，正式构建始终连接线上。
  baseURL: isDev ? DEVELOPMENT_API_ORIGIN : API_SERVERS.online,
  ...errorConfig,
};
